import type { ChannelItem, ChannelLayer, ChannelPreview, ChannelPreviewItem, ScopedLayer } from '@/types/api'
import { SLOT_SKIP_REASON_LABELS } from '@/features/content-slots/lib/skip-reasons'
import { blockPolicy, type BlockPolicy } from './merge'

/* ------------------------------------------------------------- blocks */

export interface ItemBlock {
  key: string
  layer: ChannelLayer
  scopeId: string | null
  scopeName: string | null
  policy: BlockPolicy
  items: ChannelItem[]
}

export function blockKey(layer: ChannelLayer, scopeId: string | null | undefined): string {
  return layer === 'tenant' ? 'tenant' : `${layer}:${scopeId ?? ''}`
}

/** Groups a channel's items into blocks `(layer, scope)`, items in position order. */
export function groupBlocks(items: readonly ChannelItem[]): { base: ChannelItem[]; blocks: ItemBlock[] } {
  const map = new Map<string, ItemBlock>()
  for (const item of items) {
    const scopeId = item.layer === 'tenant' ? null : (item.scope?.id ?? null)
    const key = blockKey(item.layer, scopeId)
    let block = map.get(key)
    if (!block) {
      block = { key, layer: item.layer, scopeId, scopeName: item.scope?.name ?? null, policy: { insertion: 'append' }, items: [] }
      map.set(key, block)
    }
    block.items.push(item)
  }
  for (const block of map.values()) {
    block.items.sort((a, b) => a.position - b.position || (a.id < b.id ? -1 : 1))
    block.policy = blockPolicy(block.items)
  }
  const base = map.get('tenant')?.items ?? []
  const order: ChannelLayer[] = ['location', 'group', 'display']
  const blocks = [...map.values()]
    .filter((b) => b.layer !== 'tenant')
    .sort((a, b) => order.indexOf(a.layer) - order.indexOf(b.layer) || ((a.scopeId ?? '') < (b.scopeId ?? '') ? -1 : 1))
  return { base, blocks }
}

export function blocksForLayer(blocks: readonly ItemBlock[], layer: ScopedLayer): ItemBlock[] {
  return blocks.filter((b) => b.layer === layer)
}

/* ------------------------------------------------------------- reorder */

/** Moves `id` to `toIndex` and returns the complete new order (the `/items/reorder` payload for one block). */
export function moveId(ids: readonly string[], id: string, toIndex: number): string[] {
  const from = ids.indexOf(id)
  if (from < 0) return [...ids]
  const next = ids.filter((x) => x !== id)
  const to = Math.max(0, Math.min(next.length, toIndex))
  next.splice(to, 0, id)
  return next
}

/** `{item_ids}` body for `POST /channels/{id}/items/reorder` — the full order of exactly one block. */
export function buildReorderPayload(block: readonly Pick<ChannelItem, 'id' | 'layer' | 'scope'>[], order: readonly string[]): { item_ids: string[] } {
  const ids = new Set(block.map((i) => i.id))
  if (order.length !== ids.size || order.some((id) => !ids.has(id)) || new Set(order).size !== order.length) {
    throw new Error('Reorder must contain every item of the block exactly once.')
  }
  const keys = new Set(block.map((i) => blockKey(i.layer, i.scope?.id)))
  if (keys.size > 1) throw new Error('Reorder must target a single layer and scope.')
  return { item_ids: [...order] }
}

/** Applies a new order to cached items (optimistic update): positions are rewritten 1000, 2000, … like the server. */
export function applyOrder(items: readonly ChannelItem[], order: readonly string[]): ChannelItem[] {
  const pos = new Map(order.map((id, i) => [id, (i + 1) * 1000]))
  return items.map((item) => (pos.has(item.id) ? { ...item, position: pos.get(item.id)! } : item))
}

/* ------------------------------------------------------------- durations */

/** `15000` → `15 s`, `90000` → `1 min 30 s`, `3600000` → `1 h`. */
export function formatDuration(ms: number): string {
  const total = Math.round(ms / 1000)
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = total % 60
  const parts: string[] = []
  if (h) parts.push(`${h} h`)
  if (m) parts.push(`${m} min`)
  if (s || parts.length === 0) parts.push(`${s} s`)
  return parts.join(' ')
}

export interface CycleTime {
  /** Sum of explicit durations. */
  knownMs: number
  /** Items that play one full presentation cycle (duration unknown here). */
  cycles: number
  count: number
}

export function cycleTime(items: readonly Pick<ChannelPreviewItem, 'duration_ms'>[]): CycleTime {
  let knownMs = 0
  let cycles = 0
  for (const item of items) {
    if (item.duration_ms === null || item.duration_ms === undefined) cycles++
    else knownMs += item.duration_ms
  }
  return { knownMs, cycles, count: items.length }
}

export function formatCycleTime(c: CycleTime): string {
  if (c.count === 0) return 'Nothing to play'
  const cyclesLabel = `${c.cycles} presentation cycle${c.cycles === 1 ? '' : 's'}`
  if (c.cycles === 0) return formatDuration(c.knownMs)
  if (c.knownMs === 0) return cyclesLabel
  return `${formatDuration(c.knownMs)} + ${cyclesLabel}`
}

/* ------------------------------------------------------------- preview */

export interface PreviewRow {
  item: ChannelPreviewItem
  index: number
  /** Top-level item of this channel the row came from (first path segment). */
  rootItemId: string
  nested: boolean
  /** Source channel id (lowercase), set when the slideshow lives in a nested channel. */
  viaChannelId: string | null
  /** Starts a new run of consecutive rows from the same layer (+scope). */
  groupStart: boolean
}

const lower = (s: string) => s.toLowerCase()

/** Annotates resolved items with provenance and groups consecutive rows by origin (layer + root item scope). */
export function buildPreviewRows(preview: ChannelPreview, channelId: string, items: readonly ChannelItem[]): PreviewRow[] {
  const byId = new Map(items.map((i) => [lower(i.id), i]))
  let prevKey: string | null = null
  return preview.items.map((item, index) => {
    const segments = item.id.split('/')
    const rootItemId = lower(segments[0] ?? item.id)
    const root = byId.get(rootItemId)
    const nested = segments.length > 1
    const source = lower(item.source.channel_id)
    const key = root ? blockKey(root.layer, root.scope?.id) : `src:${source}:${item.source.layer}`
    const groupStart = key !== prevKey
    prevKey = key
    return { item, index, rootItemId, nested, viaChannelId: source !== lower(channelId) ? source : null, groupStart }
  })
}

/** True when an item's validity window is open now (or unset). */
export function validityState(validFrom: string | null, validUntil: string | null, now: Date = new Date()): 'always' | 'active' | 'scheduled' | 'expired' {
  if (!validFrom && !validUntil) return 'always'
  const t = now.getTime()
  if (validUntil && new Date(validUntil).getTime() <= t) return 'expired'
  if (validFrom && new Date(validFrom).getTime() > t) return 'scheduled'
  return 'active'
}

export const LAYER_LABELS: Record<ChannelLayer, string> = {
  tenant: 'Organization',
  location: 'Location',
  group: 'Group',
  display: 'Display',
}

export const SKIP_REASON_LABELS: Record<string, string> = {
  expired: 'Validity window has ended',
  not_published: 'Slideshow isn’t published yet',
  ...SLOT_SKIP_REASON_LABELS,
}

export function itemName(item: Pick<ChannelItem, 'slideshow' | 'child_channel'> & Partial<Pick<ChannelItem, 'slot'>>): string {
  return item.slideshow?.name ?? item.child_channel?.name ?? item.slot?.name ?? 'Unknown item'
}
